import React, { useEffect, useState, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { GlassCard } from '../GlassCard';
import { GlassCTA } from '../GlassCTA';
import { useProfileStore, type OnboardingData } from '@/stores/profileStore';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface BoardPreview {
  id: string;
  name: string;
  phase: string;
  priority: number;
}

interface BoardGenStepProps {
  onboardingData: OnboardingData;
  onComplete: () => void;
}

const LOADING_MESSAGES = [
  'Setting up your workspace...',
  'Building boards around how you think...',
  'Almost ready.',
];

export function BoardGenStep({
  onboardingData,
  onComplete,
}: BoardGenStepProps) {
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [boards, setBoards] = useState<BoardPreview[]>([]);
  const [editingBoardId, setEditingBoardId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [loadingMsgIndex, setLoadingMsgIndex] = useState(0);
  const hasStarted = useRef(false);

  // Snapshot the data on first render to prevent stale-closure issues
  const dataRef = useRef(onboardingData);

  const createProfileFromOnboarding = useProfileStore(
    (s) => s.createProfileFromOnboarding
  );

  // Rotate loading messages
  useEffect(() => {
    if (!isLoading) return;
    const interval = setInterval(() => {
      setLoadingMsgIndex((prev) =>
        prev < LOADING_MESSAGES.length - 1 ? prev + 1 : prev
      );
    }, 1500);
    return () => clearInterval(interval);
  }, [isLoading]);

  const runCreation = useCallback(async () => {
    try {
      setHasError(false);
      setIsLoading(true);

      await createProfileFromOnboarding(dataRef.current);

      // Build board previews from the goals
      const profile = useProfileStore.getState().profile;
      if (profile) {
        const previews: BoardPreview[] = profile.goals
          .filter((g) => g.board_id)
          .sort((a, b) => a.priority - b.priority)
          .map((g) => ({
            id: g.board_id!,
            name: g.text,
            phase: g.phase,
            priority: g.priority,
          }));
        setBoards(previews);
      }

      setIsLoading(false);
    } catch (err: any) {
      console.error('Board generation error:', err?.message || err, err);
      toast.error(err?.message || 'Board generation failed');
      setHasError(true);
      setIsLoading(false);
    }
  }, [createProfileFromOnboarding]);

  // Run onboarding profile creation on mount
  useEffect(() => {
    if (hasStarted.current) return;
    hasStarted.current = true;
    runCreation();
  }, [runCreation]);

  const phaseEmoji = (phase: string) => {
    switch (phase) {
      case 'exploring':
        return '\uD83D\uDD0D';
      case 'planned':
        return '\uD83D\uDCCB';
      case 'executing':
        return '\uD83D\uDE80';
      default:
        return '';
    }
  };

  const startEditing = (board: BoardPreview) => {
    setEditingBoardId(board.id);
    setEditName(board.name);
  };

  const saveEdit = async (boardId: string) => {
    const trimmed = editName.trim();
    if (trimmed && trimmed !== boards.find((b) => b.id === boardId)?.name) {
      await supabase
        .from('boards')
        .update({ name: trimmed })
        .eq('id', boardId);
      setBoards((prev) =>
        prev.map((b) => (b.id === boardId ? { ...b, name: trimmed } : b))
      );
    }
    setEditingBoardId(null);
  };

  // Error state — let user retry or skip
  if (hasError) {
    return (
      <GlassCard>
        <div className="flex flex-col items-center gap-6 py-4 text-center">
          <h1
            className="font-display text-[28px] font-semibold text-ink"
          >
            Something went wrong.
          </h1>
          <p
            className="text-base"
            style={{ color: 'hsl(var(--ink-2))' }}
          >
            We couldn't finish setting up your boards. You can try again or skip
            ahead and set things up later.
          </p>
          <div className="flex flex-col items-center gap-3 mt-4">
            <GlassCTA
              onClick={() => {
                hasStarted.current = false;
                setLoadingMsgIndex(0);
                runCreation();
              }}
            >
              Try again
            </GlassCTA>
            <GlassCTA variant="secondary" onClick={onComplete}>
              Skip for now
            </GlassCTA>
          </div>
        </div>
      </GlassCard>
    );
  }

  // Loading state
  if (isLoading) {
    return (
      <GlassCard>
        <div className="flex flex-col items-center gap-8 py-8">
          {/* Shimmer loading indicator */}
          <div className="relative flex gap-2.5">
            {[0, 1, 2].map((i) => (
              <div
                key={i}
                className="h-2.5 w-2.5 rounded-full bg-signal motion-reduce:!animate-none"
                style={{
                  animation: `boardGenPulse 1.4s ease-in-out ${i * 0.2}s infinite`,
                }}
              />
            ))}
          </div>
          <AnimatePresence mode="wait">
            <motion.p
              key={loadingMsgIndex}
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -6 }}
              transition={{ duration: 0.3 }}
              className="text-base"
              style={{ color: 'hsl(var(--ink-2))' }}
            >
              {LOADING_MESSAGES[loadingMsgIndex]}
            </motion.p>
          </AnimatePresence>
          <style>{`
            @keyframes boardGenPulse {
              0%, 80%, 100% { opacity: 0.25; transform: scale(0.8); }
              40% { opacity: 1; transform: scale(1.15); }
            }
          `}</style>
        </div>
      </GlassCard>
    );
  }

  // No boards created (0 goals) — skip straight through
  if (boards.length === 0) {
    return (
      <GlassCard>
        <div className="flex flex-col items-center text-center">
          <h1
            className="font-display text-[28px] font-semibold text-ink"
          >
            You're all set.
          </h1>
          <p
            className="mt-3 text-base"
            style={{ color: 'hsl(var(--ink-2))' }}
          >
            Your workspace is ready. Add goals anytime from Partnr.
          </p>
          <div className="mt-10">
            <GlassCTA onClick={onComplete}>Let's go</GlassCTA>
          </div>
        </div>
      </GlassCard>
    );
  }

  // Preview state — boards created successfully
  return (
    <GlassCard>
      <div className="flex flex-col items-center text-center">
        <h1
          className="font-display text-[28px] font-semibold text-ink"
        >
          Here's what I set up for you.
        </h1>
        <p
          className="mt-2 text-sm"
          style={{ color: 'hsl(var(--ink-3))' }}
        >
          Click a name to rename it.
        </p>

        <div className="mt-8 flex w-full flex-col gap-3">
          {boards.map((board, i) => (
            <motion.div
              key={board.id}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.35, delay: 0.1 + i * 0.1 }}
              className="flex items-center gap-4 rounded-[14px] border border-line bg-paper-sunken p-4"
            >
              <span className="text-xl">{phaseEmoji(board.phase)}</span>
              <div className="flex-1 text-left">
                {editingBoardId === board.id ? (
                  <input
                    autoFocus
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                    onBlur={() => saveEdit(board.id)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') saveEdit(board.id);
                      if (e.key === 'Escape') setEditingBoardId(null);
                    }}
                    className="w-full border-b border-signal bg-transparent text-base font-medium text-ink outline-none"
                  />
                ) : (
                  <button
                    type="button"
                    onClick={() => startEditing(board)}
                    className="text-left text-base font-medium text-ink hover:underline"
                  >
                    {board.name}
                  </button>
                )}
                <span
                  className="mt-0.5 block text-xs"
                  style={{ color: 'hsl(var(--ink-3))' }}
                >
                  3 starter cards inside
                </span>
              </div>
              {board.priority === 1 && (
                <span
                  className="rounded-full bg-wash px-3 py-1 text-xs font-medium text-ink"
                >
                  Top priority
                </span>
              )}
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 + boards.length * 0.1 }}
          className="mt-10"
        >
          <GlassCTA onClick={onComplete}>Looks good</GlassCTA>
        </motion.div>
      </div>
    </GlassCard>
  );
}
